import { DFC_LAYOUTS, MULTI_FACE_LAYOUTS, prettifyFilename } from "@/lib/mtg";
import { planarTypeLine } from "@/lib/planar";
import { emptyFace, facePayload } from "@/lib/queries";

let seq = 0;
export const newRow = (patch = {}) => ({
  key: `row-${++seq}`, layout: "normal", faces: [emptyFace()], rarity: "common", colors: null, set_code: "",
  collector_number: "", visibility: "public", card_back_id: "", plays_as: "", frame: "custom", status: "ready", error: "", card_id: null, ...patch,
});

const stem = (name) => name.toLowerCase().replace(/\.[^.]+$/, "");
const loose = (name) => stem(name).replace(/[_\-\s]+/g, " ").trim();

function imageIndex(pool) {
  const map = new Map();
  pool.forEach((u) => {
    map.set(u.filename.toLowerCase(), u);
    map.set(stem(u.filename), u);
    map.set(loose(u.filename), u);
  });
  return (name) => (name ? map.get(name.toLowerCase()) || map.get(stem(name)) || map.get(loose(name)) : null);
}

const blankNulls = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v ?? ""]));

/** Match parsed metadata rows to uploaded images; unmatched images become their own rows. */
export function buildRows(pool, metaCards = []) {
  const find = imageIndex(pool);
  const used = new Set();
  const rows = metaCards.map((mc) => {
    const missing = [];
    const faces = mc.faces.map((f, i) => {
      const img = find(f.image_filename) || (i === 0 && !f.image_filename ? find(f.name || "") : null);
      if (f.image_filename && !img) missing.push(f.image_filename);
      if (img) used.add(img.file_id);
      return { ...emptyFace(), ...blankNulls(f), image_file_id: img?.file_id || null, image_url: img?.url || null };
    });
    const error = missing.length ? `Image not found: ${missing.join(", ")}` : "";
    return newRow({ ...mc, faces, set_code: mc.set_code || "", collector_number: mc.collector_number || "", error, status: error ? "error" : "ready" });
  });
  pool.filter((u) => !used.has(u.file_id)).forEach((u) =>
    rows.push(newRow({ faces: [{ ...emptyFace(), name: prettifyFilename(u.filename), image_file_id: u.file_id, image_url: u.url }] })),
  );
  return rows;
}

export function validateRow(row, rights) {
  if (!row.faces[0]?.name?.trim()) return "Name is required";
  if (!row.faces[0]?.image_file_id) return "Front image is missing";
  if (row.plays_as && !rights) return "Confirm image permission (above) for Plane and Phenomenon rows";
  if (DFC_LAYOUTS.includes(row.layout) && !row.faces[1]?.image_file_id) return "Back face image is required for this layout";
  if (MULTI_FACE_LAYOUTS.includes(row.layout) && row.faces[1] && !row.faces[1].name?.trim()) return "Second face needs a name";
  return "";
}

export function rowPayload(row, rights) {
  if (row.plays_as) {
    const face = facePayload(row.faces[0]);
    return { layout: "planar", faces: [{ ...face, type_line: face.type_line || planarTypeLine(row.plays_as) }], rarity: row.rarity, colors: row.colors,
      set_code: row.set_code.trim() || null, collector_number: row.collector_number || null, visibility: row.visibility, card_back_id: row.card_back_id || null,
      category: "specialty", specialty_type: row.plays_as, source_program: "External design upload (bulk)",
      external_design: { visual_frame: row.frame || "custom", rights_confirmed: !!rights } };
  }
  const multi = MULTI_FACE_LAYOUTS.includes(row.layout) && row.faces.length > 1;
  const faces = row.faces.slice(0, multi ? 2 : 1).map(facePayload);
  if (multi && !DFC_LAYOUTS.includes(row.layout)) faces[1].image_file_id = null;
  return {
    layout: row.layout, faces, rarity: row.rarity, colors: row.colors, set_code: row.set_code.trim() || null,
    collector_number: row.collector_number || null, visibility: row.visibility, card_back_id: row.card_back_id || null,
    source_program: "Planar Foundry bulk upload",
  };
}
